import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { faPlus, faFileUpload } from '@fortawesome/free-solid-svg-icons'

import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import TrashIcon from 'client/utils/icons/trash-icon'
import { OrdersContext } from 'contexts/OrdersContext'
import {
  $DCDangerColor,
  $DCDangerLightColor,
  $DCInfoColor,
  $DCInfoLightColor,
  $DCSuccessColor,
  $DCSuccessLightColor,
  DcBreadcrumbs,
  DcButton,
  DcDiv,
  DcDropDown,
  DcFlexBox,
  DcTabs,
  DcTag,
  PixelCurrency,
  PixelDate,
  PixelFactoryContext,
  PixelIcon,
  PixelPageContainer,
  PixelTable,
  Section,
  SectionContent,
  DcOverlaySlider,
} from 'pixel-base'
import queryString from 'query-string'
import styled from 'styled-components'
import {
  deletePurchase,
  fetchPurchaseStatus,
  getPurchases,
} from 'utils/api-routes.util'
import PurchaseUpload from './components/PurchaseUpload'
import PurchaseEdit from './details/components/PurchaseEdit'
import { TruncatedTextWithTooltip } from './details/components/TruncatedTextWithTooltip'
import PurchaseDetail from './details/PurchaseDetail'
import {
  IPurchaseDetails,
  Warehouse,
  IPurchaseStatus,
  ITenantInfo,
  IPixelFactory,
} from '../../../../../decl'

const breadcrumbs = [
  {
    name: 'Manage Purchases',
    active: true,
  },
]

const purchaseTableStyles = {
  headRow: {
    style: {
      minHeight: '44px',
      borderBottomWidth: '1px',
      borderBottomColor: '#e5e7eb',
    },
  },
  headCells: {
    style: {
      fontSize: '12px',
      fontWeight: 600,
      color: '#6b7280',
      paddingLeft: '12px',
      paddingRight: '12px',
      whiteSpace: 'nowrap',
    },
  },
  rows: {
    style: {
      minHeight: '52px',
      borderBottomColor: '#f3f4f6',
      '&:not(:last-of-type)': {
        borderBottomWidth: '1px',
      },
    },
  },
  cells: {
    style: {
      fontSize: '13px',
      color: '#111827',
      paddingLeft: '12px',
      paddingRight: '12px',
      alignItems: 'center',
    },
  },
}

const DateCell = styled.span`
  display: inline-block;
  white-space: nowrap;
  line-height: 1.4;
`

const DeleteIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${$DCDangerColor};
  cursor: pointer;

  &:hover {
    background-color: ${$DCDangerLightColor};
    color: ${$DCDangerColor};
  }

  &:focus-visible {
    outline: 2px solid ${$DCDangerColor};
    outline-offset: 1px;
  }
`

const getStatusColors = (status?: string) => {
  if (status === 'Draft') {
    return { text: $DCDangerColor, background: $DCDangerLightColor }
  }
  if (status === 'Received') {
    return { text: $DCSuccessColor, background: $DCSuccessLightColor }
  }
  return { text: $DCInfoColor, background: $DCInfoLightColor }
}

const Purchase = () => {
  const { state } = React.useContext(OrdersContext)
  const {
    pixelConfirm,
    showSuccess,
    showFailure,
    showPageLoader,
    hidePageLoader,
  } = React.useContext<IPixelFactory>(PixelFactoryContext)
  const navigate = useNavigate()
  const [warehousesList, setWarehousesList] = React.useState<Warehouse[]>([])
  const [purchases, setPurchases] = React.useState<IPurchaseDetails[]>([])
  const [tableLoading, setTableLoader] = React.useState(true)
  const [showDetailModal, setShowDetailModal] = React.useState(false)
  const [reload, setReload] = React.useState(false)
  const [detailType, setDetailType] = React.useState('')
  const [purchaseStatus, setPurchaseStatus] = React.useState<IPurchaseStatus[]>(
    [],
  )
  const [purchaseId, setPurchaseId] = React.useState<string | undefined>(
    undefined,
  )
  const [warehouseId, setWarehouseId] = React.useState<string | undefined>(
    undefined,
  )
  const [action, setAction] = React.useState('')
  const [showUploadModal, setShowUploadModal] = React.useState(false)
  const p = queryString.parse(window.location.search)
  const [filters, setFilters] = React.useState({
    status_id: p.status
      ? Array.isArray(p.status)
        ? p.status[0]
        : p.status
      : null,
    warehouse_id: p.warehouse_id ? Number(p.warehouse_id) : null,
  })

  const getAllPurchases = () => {
    setTableLoader(true)
    getPurchases(filters).subscribe((res) => {
      setTableLoader(false)
      const response = res.response as { data: IPurchaseDetails[] }
      if (reload) {
        setReload(false)
      }
      setPurchases(response.data)
    })
  }
  const fetchAllPurchaseStatus = () => {
    fetchPurchaseStatus().subscribe((res) => {
      const response = res.response as { data: IPurchaseStatus[] }
      setPurchaseStatus(response.data)
    })
  }
  React.useEffect(() => {
    fetchAllPurchaseStatus()
  }, [])
  useEffect(() => {
    setWarehousesList(state?.warehouses || [])
  }, [state])
  React.useEffect(() => {
    navigate({
      pathname: '/inventory/purchases',
      search: queryString.stringify(filters),
    })
    getAllPurchases()
  }, [filters, reload])
  const tenant_info: ITenantInfo = JSON.parse(
    localStorage.getItem('tenant_info') || '{}',
  )

  const handleDeletePurchase = (
    event: React.MouseEvent,
    row: IPurchaseDetails,
  ) => {
    event.stopPropagation()
    if (row.purchase_status?.status !== 'Draft') {
      return
    }

    pixelConfirm({
      title: 'Delete Purchase',
      message:
        'Are you sure you want to delete this draft purchase? This action cannot be undone.',
      onConfirm: () => {
        showPageLoader()
        deletePurchase(row.id).subscribe({
          next: (res) => {
            hidePageLoader()
            const response = res.response as {
              message?: string
              error?: string
            }
            if (response?.error) {
              showFailure(
                typeof response.error === 'string'
                  ? response.error
                  : 'Failed to delete purchase',
              )
              return
            }
            showSuccess(response?.message || 'Purchase deleted successfully')
            setReload(true)
          },
          error: () => {
            hidePageLoader()
            showFailure('Failed to delete purchase')
          },
        })
      },
    })
  }

  const columns = [
    {
      id: 'created_at',
      name: 'Date',
      width: '160px',
      minWidth: '150px',
      maxWidth: '170px',
      grow: 0,
      wrap: false,
      sortable: true,
      selector: (row: IPurchaseDetails) => (
        <DateCell>
          <PixelDate
            className=""
            format={'dateWithTime'}
            value={row.created_at?.date}
          />
        </DateCell>
      ),
    },
    {
      id: 'warehouse',
      name: 'Warehouse',
      minWidth: '160px',
      grow: 2,
      wrap: false,
      sortable: true,
      selector: (row: IPurchaseDetails) => (
        <TruncatedTextWithTooltip text={row.warehouse?.name || '--'} />
      ),
    },
    {
      id: 'reference_number',
      name: 'PO Number',
      minWidth: '110px',
      grow: 1,
      wrap: false,
      sortable: true,
      selector: (row: IPurchaseDetails) => (
        <TruncatedTextWithTooltip text={row.reference_number || '--'} />
      ),
    },
    {
      id: 'supplier',
      name: 'Supplier',
      minWidth: '160px',
      grow: 2,
      wrap: false,
      sortable: true,
      selector: (row: IPurchaseDetails) => (
        <TruncatedTextWithTooltip text={row.supplier?.supplier_name || '--'} />
      ),
    },
    {
      id: 'purchase_status',
      name: 'Status',
      width: '110px',
      minWidth: '100px',
      maxWidth: '120px',
      grow: 0,
      sortable: true,
      selector: (row: IPurchaseDetails) => {
        const status = row.purchase_status?.status
        const colors = getStatusColors(status)
        return (
          <DcTag
            textColor={colors.text}
            customBackgroundColor={colors.background}
          >
            {status || '--'}
          </DcTag>
        )
      },
    },
    {
      id: 'total_amount',
      name: `Total (${tenant_info.default_currency_symbol || ''})`,
      width: '120px',
      minWidth: '110px',
      maxWidth: '140px',
      grow: 0,
      wrap: false,
      sortable: true,
      selector: (row: IPurchaseDetails) => (
        <PixelCurrency hideSymbol={true} value={row.total_amount} />
      ),
    },
    {
      id: 'total_item_count',
      name: 'Items',
      width: '72px',
      minWidth: '64px',
      maxWidth: '80px',
      grow: 0,
      center: true,
      sortable: true,
      selector: (row: IPurchaseDetails) => row.total_item_count ?? '--',
    },
    {
      id: 'created_by',
      name: 'Created By',
      minWidth: '120px',
      grow: 1,
      wrap: false,
      sortable: true,
      selector: (row: IPurchaseDetails) => {
        const createdBy =
          typeof row.user === 'string'
            ? row.user
            : (row as IPurchaseDetails & { creator_name?: string }).creator_name
        return <TruncatedTextWithTooltip text={createdBy || '--'} />
      },
    },
    {
      id: 'actions',
      name: '',
      width: '40px',
      minWidth: '40px',
      maxWidth: '40px',
      grow: 0,
      center: true,
      compact: true,
      button: true,
      ignoreRowClick: true,
      sortable: false,
      selector: (row: IPurchaseDetails) =>
        row.purchase_status?.status === 'Draft' ? (
          <DeleteIconButton
            type="button"
            aria-label="Delete draft purchase"
            title="Delete"
            onClick={(e) => handleDeletePurchase(e, row)}
          >
            <TrashIcon size={15} />
          </DeleteIconButton>
        ) : null,
    },
  ]

  const handleButtonClick = (row) => {
    setPurchaseId(row.id)
    setShowDetailModal(true)
    setWarehouseId(row.warehouse.id)
    if (row.purchase_status?.status === 'Draft') {
      setDetailType('return')
      setAction('Edit Purchase')
    } else {
      setDetailType('detail')
      setAction('Purchase Details')
    }
  }

  return (
    <>
      <PixelPageContainer maxHeight="100vh" height="100vh" padding="0px">
        <DcFlexBox
          height="60px"
          backgroundColor="white"
          style={{
            fontSize: '16px',
            borderBottom: '1px solid #e5e5e5',
            padding: '8px 0px',
          }}
        >
          <DcFlexBox
            justifyContent="space-between"
            alignItems="center"
            padding=".5rem 20px"
          >
            <DcBreadcrumbs fontSize="16px" data={breadcrumbs} />
            <ProfileSection />
          </DcFlexBox>
        </DcFlexBox>
        <SectionContent style={{ padding: '0px', borderRadius: '0px' }}>
          <DcFlexBox
            height="auto"
            padding="0 20px"
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <DcFlexBox alignItems="flex-end" height="auto">
              <DcTabs
                size="md"
                tabs={[...warehousesList].map((warehouse: any) => {
                  return {
                    label: warehouse.name,
                    value: warehouse.wh_id,
                  }
                })}
                variant="pills"
                activeTab={filters.warehouse_id?.toString() || null}
                handleChange={(value) => {
                  setFilters({
                    ...filters,
                    warehouse_id: value,
                  })
                }}
              />
            </DcFlexBox>
            <DcFlexBox
              gap="5px"
              height="auto"
              width="50%"
              justifyContent="flex-end"
              padding="5px 0px"
            >
              <DcDiv height="auto" width="30%">
                <DcDropDown
                  dropdownSize="sm"
                  placeholder="Select Status"
                  customLabel=""
                  options={[
                    {
                      status: 'Select Status',
                      id: '',
                    },
                    ...purchaseStatus,
                  ].map((item: any) => {
                    return {
                      label: item.status,
                      value: item.id,
                    }
                  })}
                  value={filters.status_id || ''}
                  onChange={(e) => {
                    setFilters({
                      ...filters,
                      status_id: e.target.value,
                    })
                  }}
                />
              </DcDiv>
              <DcDiv height="auto">
                <DcButton
                  size="sm"
                  variant="outline-primary"
                  onClick={() => {
                    setShowUploadModal(true)
                  }}
                >
                  <PixelIcon color="#007bff" icon={faFileUpload} />
                  &nbsp; Bulk Import
                </DcButton>
              </DcDiv>
              <DcDiv height="auto">
                <DcButton
                  size="sm"
                  onClick={() => {
                    setDetailType('new')
                    setShowDetailModal(true)
                    setAction('New Purchase')
                    setPurchaseId(undefined)
                    setWarehouseId(
                      filters.warehouse_id?.toString() || undefined,
                    )
                  }}
                >
                  <PixelIcon color="white" icon={faPlus} />
                  &nbsp; New Purchase
                </DcButton>
              </DcDiv>
            </DcFlexBox>
          </DcFlexBox>
        </SectionContent>
        <Section style={{ padding: '20px' }}>
          <SectionContent>
            <PixelTable
              columns={columns}
              data={purchases}
              customStyles={purchaseTableStyles}
              highlightOnHover={true}
              pointerOnHover={true}
              isSearchable={true}
              fixedHeader
              fixedHeaderScrollHeight="calc(100vh - 212px)"
              progressPending={tableLoading}
              onRowClicked={(row) => handleButtonClick(row)}
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>

      <DcOverlaySlider
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false)
        }}
        title={action}
        width="70%"
        padding="0px"
      >
        {detailType === 'detail' ? (
          <PurchaseDetail purchaseId={purchaseId || ''} />
        ) : (
          <PurchaseEdit
            purchase_id={purchaseId || ''}
            warehouse_id={warehouseId || ''}
            action={action}
            setShowDetailModal={setShowDetailModal}
            setPurchaseId={setPurchaseId}
            detailType={detailType}
            setDetailType={setDetailType}
            reload={reload}
            setReload={setReload}
          />
        )}
      </DcOverlaySlider>

      <DcOverlaySlider
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false)
        }}
        title="Bulk Import Purchase Data"
        width="50%"
        padding="0px"
      >
        <PurchaseUpload
          warehousesList={warehousesList}
          onClose={() => {
            setShowUploadModal(false)
          }}
          onUploadSuccess={() => {
            setReload(true)
            getAllPurchases()
          }}
        />
      </DcOverlaySlider>
    </>
  )
}

export default Purchase
